import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = '/api';
const RED = '#b91d47', RED_D = '#8f1538', INK = '#1c1c28', GREY = '#7a7a8c', GREEN = '#22a06b';

export default function AppSalonDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [salon, setSalon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]); // selected services
  const [showReview, setShowReview] = useState(false);
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    // Salon was stashed in sessionStorage by home for instant load
    const cached = sessionStorage.getItem('app_salon_' + id);
    if (cached) {
      setSalon(JSON.parse(cached));
      setLoading(false);
      return;
    }
    // Fallback: fetch nearby and find it
    const load = async () => {
      try {
        const r = await axios.get(`${API}/salons/nearby`, { params: { lat: 24.95, lng: 84.08, radius: 50 }, timeout: 12000 });
        const list = Array.isArray(r.data) ? r.data : (r.data?.salons || []);
        const found = list.find(s => (s.salon_id || s.id) === id);
        setSalon(found || null);
      } catch (e) { setSalon(null); }
      setLoading(false);
    };
    load();
  }, [id]);

  useEffect(() => {
    try {
      const list = JSON.parse(localStorage.getItem('savedSalons') || '[]');
      setSaved(list.some(s => (s.salon_id || s.id) === id));
    } catch (e) {}
  }, [id]);

  const submitReview = async () => {
    if (rating === 0) { toast.error('Please select a rating'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/review/create`, {
        salon_id: salon.salon_id || salon.id,
        customer_phone: localStorage.getItem('userPhone') || '',
        customer_name: localStorage.getItem('userName') || 'Customer',
        rating,
        review_text: reviewText.trim(),
      }, { timeout: 10000 });
      toast.success('Thank you for your review!');
      setShowReview(false); setRating(0); setReviewText('');
    } catch (e) {
      toast.error('Could not submit review. Try again.');
    }
    setSubmitting(false);
  };

  const toggleSave = () => {
    try {
      const list = JSON.parse(localStorage.getItem('savedSalons') || '[]');
      const sid = salon.salon_id || salon.id;
      const exists = list.some(s => (s.salon_id || s.id) === sid);
      let next;
      if (exists) { next = list.filter(s => (s.salon_id || s.id) !== sid); setSaved(false); toast.success('Removed from saved'); }
      else { next = [...list, salon]; setSaved(true); toast.success('Saved!'); }
      localStorage.setItem('savedSalons', JSON.stringify(next));
    } catch (e) {}
  };

  if (loading) return <div style={{ padding: 60, textAlign: 'center', color: GREY }}>Loading...</div>;
  if (!salon) return (
    <div style={{ padding: 40, textAlign: 'center', color: GREY }}>
      Salon nahi mila.
      <div><button onClick={() => navigate('/app/home')} style={{ marginTop: 16, background: RED, color: '#fff', border: 'none', borderRadius: 10, padding: '12px 24px', fontWeight: 700 }}>Home</button></div>
    </div>
  );

  // Services: support array of {name, price} or object
  let services = salon.services || [];
  if (!Array.isArray(services)) services = Object.values(services);
  if (services.length === 0) {
    services = [
      { name: 'Haircut', price: 100, duration: '30 min' },
      { name: 'Beard Trim', price: 50, duration: '15 min' },
      { name: 'Hair Color', price: 400, duration: '60 min' },
      { name: 'Facial', price: 300, duration: '45 min' },
    ];
  }

  const gallery = salon.gallery || salon.gallery_photos || [];
  const total = selected.reduce((sum, i) => sum + (Number(services[i]?.price) || 0), 0);

  const toggle = (i) => {
    setSelected(prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]);
  };

  const proceedToBook = () => {
    if (selected.length === 0) { toast.error('Kam se kam ek service chunein'); return; }
    const chosen = selected.map(i => services[i]);
    sessionStorage.setItem('app_booking', JSON.stringify({
      salon_id: salon.salon_id || salon.id,
      salon_name: salon.salon_name || salon.name,
      salon_phone: salon.phone || salon.owner_phone || '',
      services: chosen, total,
    }));
    navigate('/app/book');
  };

  return (
    <div style={{ minHeight: '100vh', background: '#fff', maxWidth: 480, margin: '0 auto', position: 'relative', paddingBottom: 90 }}>
      {/* HERO */}
      <div style={{ height: 290, background: salon.photo_url ? `url(${salon.photo_url}) center/cover` : 'linear-gradient(135deg,#e2c4b8,#b98a76)', position: 'relative' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top,rgba(0,0,0,.5),transparent 50%)' }} />
        <div onClick={() => navigate('/app/home')} style={{ position: 'absolute', top: 16, left: 14, width: 40, height: 40, background: 'rgba(0,0,0,.4)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 22, cursor: 'pointer', zIndex: 5 }}>‹</div>
        <div style={{ position: 'absolute', bottom: 30, left: 16, right: 16, color: '#fff', zIndex: 5 }}>
          <h2 style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em' }}>{salon.salon_name || salon.name}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, fontSize: 12 }}>
            <span style={{ background: GREEN, color: '#fff', fontWeight: 800, padding: '3px 8px', borderRadius: 6 }}>{salon.rating || '4.2'} ★</span>
            <span>📍 {salon.distanceKm ? `${salon.distanceKm.toFixed(1)} km` : 'Near you'}</span>
            <span style={{ color: '#7fffb0', fontWeight: 700 }}>● Open</span>
          </div>
        </div>
        {/* Directions button */}
        <div style={{ position: 'absolute', top: 16, right: 14, display: 'flex', gap: 8, zIndex: 5 }}>
          <div onClick={toggleSave} style={{ background: 'rgba(255,255,255,.95)', color: RED, borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,.2)' }}>
            {saved ? '♥' : '♡'}
          </div>
          <div onClick={() => {
            const la = salon.latitude || salon.lat, ln = salon.longitude || salon.lng;
            if (la && ln) window.open(`https://www.google.com/maps/dir/?api=1&destination=${la},${ln}`, '_blank');
            else toast.error('Location not available');
          }} style={{ background: 'rgba(255,255,255,.95)', color: RED, borderRadius: 22, padding: '9px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, boxShadow: '0 4px 12px rgba(0,0,0,.2)' }}>
            🧭 Directions
          </div>
        </div>
      </div>

      <div style={{ background: '#fff', borderRadius: '24px 24px 0 0', marginTop: -24, position: 'relative', padding: '20px 16px 16px', zIndex: 6 }}>
        {/* GALLERY */}
        {gallery.length > 0 && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 18, overflowX: 'auto' }}>
            {gallery.slice(0, 8).map((g, i) => (
              <img key={i} src={g} alt="" style={{ flex: '0 0 auto', width: 70, height: 70, borderRadius: 12, objectFit: 'cover' }} />
            ))}
          </div>
        )}

        <h3 style={{ fontSize: 16, fontWeight: 800, margin: '2px 0 14px', color: INK }}>Services</h3>
        {services.map((s, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: 14, padding: 15, display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 11, boxShadow: '0 2px 10px rgba(28,28,40,.05)', border: selected.includes(i) ? `1.5px solid ${RED}` : '1.5px solid #f0f0f4' }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: INK }}>{s.name}</div>
              <div style={{ fontSize: 11, color: GREY, marginTop: 2 }}>{s.duration || '~30 min'}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: INK }}>₹{s.price}</div>
              <button onClick={() => toggle(i)} style={{ border: `1.5px solid ${RED}`, color: selected.includes(i) ? '#fff' : RED, background: selected.includes(i) ? RED : '#fff', borderRadius: 9, padding: '7px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                {selected.includes(i) ? '✓ Added' : 'Add'}
              </button>
            </div>
          </div>
        ))}

        {/* REVIEWS */}
        <div style={{ marginTop: 24, borderTop: '1px solid #f0f0f4', paddingTop: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
            <h3 style={{ fontSize: 16, fontWeight: 800, color: INK }}>Rate this salon</h3>
            {!showReview && (
              <button onClick={() => setShowReview(true)} style={{ marginLeft: 'auto', background: '#fdf2f5', color: RED, border: 'none', borderRadius: 20, padding: '7px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Write a review</button>
            )}
          </div>

          {showReview && (
            <div style={{ background: '#fff', borderRadius: 14, padding: 16, boxShadow: '0 2px 10px rgba(28,28,40,.05)', marginBottom: 90 }}>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 14 }}>
                {[1, 2, 3, 4, 5].map(n => (
                  <span key={n} onClick={() => setRating(n)} style={{ fontSize: 36, cursor: 'pointer', color: n <= rating ? '#ff8a00' : '#e0e0e0' }}>★</span>
                ))}
              </div>
              <textarea value={reviewText} onChange={e => setReviewText(e.target.value)} placeholder="Share your experience (optional)" rows={3}
                style={{ width: '100%', border: '1.5px solid #f0f0f4', borderRadius: 12, padding: 12, fontSize: 14, outline: 'none', boxSizing: 'border-box', resize: 'none', fontFamily: 'inherit' }} />
              <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                <button onClick={() => { setShowReview(false); setRating(0); setReviewText(''); }} style={{ flex: 1, background: '#fff', color: GREY, border: '1.5px solid #f0f0f4', borderRadius: 12, padding: 13, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
                <button onClick={submitReview} disabled={submitting} style={{ flex: 2, background: `linear-gradient(135deg,${RED},${RED_D})`, color: '#fff', border: 'none', borderRadius: 12, padding: 13, fontWeight: 700, cursor: 'pointer', opacity: submitting ? 0.7 : 1 }}>{submitting ? 'Submitting...' : 'Submit Review'}</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* FIXED BOOK BAR */}
      {selected.length > 0 && (
        <div style={{ position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: 480, background: '#fff', borderTop: '1px solid #f0f0f4', padding: '13px 16px', display: 'flex', gap: 12, alignItems: 'center', zIndex: 10, boxShadow: '0 -4px 20px rgba(0,0,0,.08)' }}>
          <div>
            <div style={{ fontSize: 19, fontWeight: 800, color: INK }}>₹{total}</div>
            <div style={{ fontSize: 11, color: GREY }}>{selected.length} service{selected.length > 1 ? 's' : ''} added</div>
          </div>
          <button onClick={proceedToBook} style={{ marginLeft: 'auto', background: `linear-gradient(135deg,${RED},${RED_D})`, color: '#fff', border: 'none', borderRadius: 12, padding: '14px 32px', fontSize: 15, fontWeight: 700, cursor: 'pointer', boxShadow: '0 6px 16px rgba(185,29,71,.3)' }}>Book →</button>
        </div>
      )}
    </div>
  );
}
